import { logger } from "@formant/core/common/logger";

import { IHasPeerConnection } from "./IHasPeerConnection";
import { IRtcStreamMessage } from "./models/IRtcStreamMessage";
import { encodeStreamId } from "./utils/encodeStreamId";

type IRtcConnectionV1 = IHasPeerConnection;

export class RtcSessionMetricsV1 {
    private readonly sentMessagesCounts: Map<string, number>;
    private readonly receivedMessagesCounts: Map<string, number>;
    private readonly sessionId: string;
    private readonly connection: IHasPeerConnection;

    constructor(sessionId: string, connection: IRtcConnectionV1) {
        this.sentMessagesCounts = new Map<string, number>();
        this.receivedMessagesCounts = new Map<string, number>();
        this.sessionId = sessionId;
        this.connection = connection;
    }

    public incrementMessageSent(message: IRtcStreamMessage) {
        const streamId = encodeStreamId(message.header.stream);
        this.sentMessagesCounts.set(
            streamId,
            (this.sentMessagesCounts.get(streamId) || 0) + 1,
        );
    }

    public incrementMessageReceived(message: IRtcStreamMessage) {
        const streamId = encodeStreamId(message.header.stream);
        this.receivedMessagesCounts.set(
            streamId,
            (this.receivedMessagesCounts.get(streamId) || 0) + 1,
        );
    }

    public async uploadMetrics() {
        const { sessionId } = this;
        const statsReport = await this.connection.peerConnection.getStats();
        const unpackedStats: { [id: string]: string } = {};
        statsReport.forEach(stat => (unpackedStats[stat.id] = stat));
        logger.debug("rtc-stats", {
            ...unpackedStats,
            sessionId,
        });

        const meta = [
            ...new Set(
                Array.from(this.sentMessagesCounts.keys()).concat(
                    Array.from(this.receivedMessagesCounts.keys()),
                ),
            ),
        ].reduce(
            (acc, streamId) => {
                const sentMessagesCount = this.sentMessagesCounts.get(streamId);
                const receivedMessagesCount =
                    this.receivedMessagesCounts.get(streamId);
                return {
                    ...acc,
                    [`${streamId}-sent`]: sentMessagesCount,
                    [`${streamId}-received`]: receivedMessagesCount,
                };
            },
            { sessionId },
        );
        logger.debug("rtc-message-report", meta);
    }
}
